import { subtractPurchasedItems } from '../utils/cartReconciliation';

const DATABASE_NAME = 'koral-krush-cart';
const DATABASE_VERSION = 1;
const RECORD_STORE_NAME = 'records';
const CART_RECORD_KEY = 'cart:v1';
const LEGACY_MIGRATION_KEY = 'migration:local-storage-cart:v1';
const LEGACY_CART_STORAGE_KEY = 'koral_krush_cart';
const CART_SCHEMA_VERSION = 1;
const RECONCILIATION_SCHEMA_VERSION = 1;

export class CartPersistenceError extends Error {
  constructor(message, cause) {
    super(message);
    this.name = 'CartPersistenceError';
    if (cause !== undefined) this.cause = cause;
  }
}

export class CartReconciliationConflictError extends CartPersistenceError {
  constructor(message) {
    super(message);
    this.name = 'CartReconciliationConflictError';
  }
}

function cloneValue(value) {
  if (value === undefined) return undefined;
  if (typeof window !== 'undefined' && typeof window.structuredClone === 'function') {
    return window.structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value));
}

function requireNonEmptyString(value, field, maximumLength = 512) {
  if (
    typeof value !== 'string' ||
    value.trim().length === 0 ||
    value.length > maximumLength
  ) {
    throw new CartPersistenceError(`${field} is invalid.`);
  }
  return value;
}

function validateModifiers(modifiers, field) {
  if (!Array.isArray(modifiers)) {
    throw new CartPersistenceError(`${field} must be an array.`);
  }
  modifiers.forEach((modifier, index) => {
    if (!modifier || typeof modifier !== 'object' || Array.isArray(modifier)) {
      throw new CartPersistenceError(`${field}[${index}] is invalid.`);
    }
    requireNonEmptyString(modifier.groupID, `${field}[${index}].groupID`);
    requireNonEmptyString(modifier.optionID, `${field}[${index}].optionID`);
  });
}

function validateCartItems(items) {
  if (!Array.isArray(items)) {
    throw new CartPersistenceError('The persisted cart is malformed.');
  }
  items.forEach((item, index) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      throw new CartPersistenceError(`cart.items[${index}] is invalid.`);
    }
    requireNonEmptyString(item.id, `cart.items[${index}].id`);
    requireNonEmptyString(item.productID, `cart.items[${index}].productID`);
    if (!Number.isSafeInteger(item.quantity) || item.quantity <= 0) {
      throw new CartPersistenceError(`cart.items[${index}].quantity is invalid.`);
    }
    validateModifiers(
      item.selectedModifiers ?? [],
      `cart.items[${index}].selectedModifiers`,
    );
  });
  return cloneValue(items);
}

function emptyCartRecord() {
  return {
    key: CART_RECORD_KEY,
    recordType: 'cart',
    schemaVersion: CART_SCHEMA_VERSION,
    revision: 0,
    items: [],
  };
}

function parseCartRecord(record) {
  if (record == null) return null;
  if (
    record.key !== CART_RECORD_KEY ||
    record.recordType !== 'cart' ||
    record.schemaVersion !== CART_SCHEMA_VERSION ||
    !Number.isSafeInteger(record.revision) ||
    record.revision < 0
  ) {
    throw new CartPersistenceError('The persisted cart schema is invalid.');
  }
  return {
    ...cloneValue(record),
    items: validateCartItems(record.items),
  };
}

function publicCart(record) {
  const parsed = parseCartRecord(record) || emptyCartRecord();
  return { items: cloneValue(parsed.items), revision: parsed.revision };
}

function parseLegacyCart(rawValue) {
  if (rawValue == null || rawValue === '') return [];
  let parsed;
  try {
    parsed = JSON.parse(rawValue);
  } catch (error) {
    throw new CartPersistenceError(
      'The legacy cart could not be migrated because it is not valid JSON.',
      error,
    );
  }
  return validateCartItems(parsed?.items);
}

function cartRecordWithItems(current, items) {
  const parsed = parseCartRecord(current) || emptyCartRecord();
  if (parsed.revision >= Number.MAX_SAFE_INTEGER) {
    throw new CartPersistenceError('The cart revision can no longer be advanced safely.');
  }
  return {
    ...parsed,
    revision: parsed.revision + 1,
    items: validateCartItems(items),
  };
}

function modifierConfiguration(modifiers) {
  return modifiers
    .map(modifier => [modifier.groupID, modifier.optionID])
    .sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right)));
}

function cartConfigurationKey(item) {
  return JSON.stringify([
    item.productID,
    modifierConfiguration(item.selectedModifiers || []),
  ]);
}

function validatePurchasedItems(purchasedItems) {
  if (!Array.isArray(purchasedItems) || purchasedItems.length === 0) {
    throw new CartPersistenceError('Confirmed order items are required for cart reconciliation.');
  }
  const aggregate = new Map();
  purchasedItems.forEach((item, index) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      throw new CartPersistenceError(`purchasedItems[${index}] is invalid.`);
    }
    const productID = requireNonEmptyString(
      item.productID ?? item.productId,
      `purchasedItems[${index}].productID`,
    );
    if (!Number.isSafeInteger(item.quantity) || item.quantity <= 0) {
      throw new CartPersistenceError(`purchasedItems[${index}].quantity is invalid.`);
    }
    const selectedOptions = item.selectedOptions ?? [];
    if (!Array.isArray(selectedOptions)) {
      throw new CartPersistenceError(`purchasedItems[${index}].selectedOptions is invalid.`);
    }
    const optionIDs = selectedOptions.map((option, optionIndex) => {
      if (!option || typeof option !== 'object' || Array.isArray(option)) {
        throw new CartPersistenceError(
          `purchasedItems[${index}].selectedOptions[${optionIndex}] is invalid.`,
        );
      }
      return requireNonEmptyString(
        option.id ?? option.optionID ?? option.optionId,
        `purchasedItems[${index}].selectedOptions[${optionIndex}].id`,
      );
    }).sort();
    const key = JSON.stringify([productID, optionIDs]);
    const nextQuantity = (aggregate.get(key) || 0) + item.quantity;
    if (!Number.isSafeInteger(nextQuantity)) {
      throw new CartPersistenceError('Confirmed order quantities exceed the safe integer range.');
    }
    aggregate.set(key, nextQuantity);
  });

  return JSON.stringify(
    [...aggregate.entries()]
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([configuration, quantity]) => [JSON.parse(configuration), quantity]),
  );
}

function reconciliationRecordKey(userId, referenceType, referenceId) {
  const uid = requireNonEmptyString(userId, 'userId', 256);
  const type = requireNonEmptyString(referenceType, 'referenceType', 32);
  if (type !== 'quote' && type !== 'order') {
    throw new CartPersistenceError('referenceType must be quote or order.');
  }
  const id = requireNonEmptyString(referenceId, 'referenceId', 512);
  return `reconciliation:v1:${encodeURIComponent(uid)}:${type}:${encodeURIComponent(id)}`;
}

function defaultCartItemId() {
  const cryptoObject = typeof window === 'undefined' ? null : window.crypto;
  if (typeof cryptoObject?.randomUUID === 'function') {
    return cryptoObject.randomUUID();
  }
  if (typeof cryptoObject?.getRandomValues !== 'function') {
    throw new CartPersistenceError('A cryptographically secure cart item ID could not be created.');
  }
  const bytes = cryptoObject.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map(value => value.toString(16).padStart(2, '0'));
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10).join(''),
  ].join('-');
}

function browserLegacyStorage() {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Minimal transaction adapter over one IndexedDB object store. Every key is
 * read before the synchronous operation runs, and the promise resolves only
 * after the browser reports that the whole transaction committed.
 */
export function createIndexedDbCartTransactionAdapter({
  indexedDBFactory = typeof window === 'undefined' ? null : window.indexedDB,
  databaseName = DATABASE_NAME,
} = {}) {
  let databasePromise = null;

  const openDatabase = () => {
    if (databasePromise) return databasePromise;
    databasePromise = new Promise((resolve, reject) => {
      if (!indexedDBFactory || typeof indexedDBFactory.open !== 'function') {
        reject(new CartPersistenceError('IndexedDB is required to use the cart.'));
        return;
      }
      const request = indexedDBFactory.open(databaseName, DATABASE_VERSION);
      let blocked = false;
      request.onupgradeneeded = () => {
        const database = request.result;
        if (!database.objectStoreNames.contains(RECORD_STORE_NAME)) {
          database.createObjectStore(RECORD_STORE_NAME, { keyPath: 'key' });
        }
      };
      request.onblocked = () => {
        blocked = true;
        reject(new CartPersistenceError('The cart database upgrade is blocked by another tab.'));
      };
      request.onerror = () => {
        reject(new CartPersistenceError('The cart database could not be opened.', request.error));
      };
      request.onsuccess = () => {
        if (blocked) {
          request.result.close();
          return;
        }
        const database = request.result;
        database.onversionchange = () => database.close();
        resolve(database);
      };
    }).catch(error => {
      databasePromise = null;
      throw error;
    });
    return databasePromise;
  };

  const transact = async (keys, mode, operation) => {
    const database = await openDatabase();
    const uniqueKeys = [...new Set(keys)];
    if (!uniqueKeys.every(key => typeof key === 'string' && key.length > 0)) {
      throw new CartPersistenceError('A cart transaction key is invalid.');
    }
    if (mode !== 'readonly' && mode !== 'readwrite') {
      throw new CartPersistenceError('A cart transaction mode is invalid.');
    }
    if (typeof operation !== 'function') {
      throw new CartPersistenceError('A cart transaction operation is required.');
    }

    return new Promise((resolve, reject) => {
      let transaction;
      try {
        transaction = database.transaction(RECORD_STORE_NAME, mode);
      } catch (error) {
        reject(new CartPersistenceError('The cart transaction could not start.', error));
        return;
      }
      const store = transaction.objectStore(RECORD_STORE_NAME);
      const records = new Map();
      const writes = new Map();
      let operationResult;
      let failure = null;
      let operationRan = false;

      const abortWith = error => {
        failure = error instanceof Error
          ? error
          : new CartPersistenceError('The cart transaction failed.');
        try {
          transaction.abort();
        } catch {
          // The transaction might already have been aborted by IndexedDB.
        }
      };

      const runOperation = () => {
        if (operationRan) return;
        operationRan = true;
        const view = {
          get(key) {
            if (!uniqueKeys.includes(key)) {
              throw new CartPersistenceError('The transaction read an undeclared key.');
            }
            return cloneValue(records.get(key));
          },
          put(record) {
            if (mode !== 'readwrite') {
              throw new CartPersistenceError('A readonly cart transaction cannot write.');
            }
            if (!record || !uniqueKeys.includes(record.key)) {
              throw new CartPersistenceError('The transaction wrote an undeclared key.');
            }
            writes.set(record.key, cloneValue(record));
          },
          delete(key) {
            if (mode !== 'readwrite' || !uniqueKeys.includes(key)) {
              throw new CartPersistenceError('The transaction deleted an undeclared key.');
            }
            writes.set(key, null);
          },
        };
        try {
          operationResult = operation(view);
          if (operationResult && typeof operationResult.then === 'function') {
            throw new CartPersistenceError('Cart transaction operations must be synchronous.');
          }
          writes.forEach((record, key) => {
            if (record == null) store.delete(key);
            else store.put(record);
          });
        } catch (error) {
          abortWith(error);
        }
      };

      transaction.oncomplete = () => resolve(cloneValue(operationResult));
      transaction.onabort = () => reject(
        failure || new CartPersistenceError('The cart transaction was aborted.', transaction.error),
      );
      transaction.onerror = () => {
        if (!failure) {
          failure = new CartPersistenceError('The cart transaction failed.', transaction.error);
        }
      };

      if (uniqueKeys.length === 0) {
        runOperation();
        return;
      }
      let remaining = uniqueKeys.length;
      uniqueKeys.forEach(key => {
        const request = store.get(key);
        request.onsuccess = () => {
          if (request.result !== undefined) records.set(key, request.result);
          remaining -= 1;
          if (remaining === 0) runOperation();
        };
        request.onerror = () => abortWith(
          new CartPersistenceError('A cart record could not be read.', request.error),
        );
      });
    });
  };

  return {
    transact,
    async close() {
      if (!databasePromise) return;
      const database = await databasePromise;
      database.close();
      databasePromise = null;
    },
  };
}

/** In-memory serializable adapter used for deterministic concurrency tests. */
export function createMemoryCartTransactionAdapter({
  initialRecords = [],
  beforeCommit = null,
} = {}) {
  const records = new Map(initialRecords.map(record => [record.key, cloneValue(record)]));
  let tail = Promise.resolve();
  let nextWriteFailure = null;

  const transact = (keys, mode, operation) => {
    const uniqueKeys = [...new Set(keys)];
    const run = async () => {
      const writes = new Map();
      const view = {
        get(key) {
          if (!uniqueKeys.includes(key)) {
            throw new CartPersistenceError('The transaction read an undeclared key.');
          }
          return cloneValue(records.get(key));
        },
        put(record) {
          if (mode !== 'readwrite' || !record || !uniqueKeys.includes(record.key)) {
            throw new CartPersistenceError('The transaction wrote an undeclared key.');
          }
          writes.set(record.key, cloneValue(record));
        },
        delete(key) {
          if (mode !== 'readwrite' || !uniqueKeys.includes(key)) {
            throw new CartPersistenceError('The transaction deleted an undeclared key.');
          }
          writes.set(key, null);
        },
      };
      const result = operation(view);
      if (result && typeof result.then === 'function') {
        throw new CartPersistenceError('Cart transaction operations must be synchronous.');
      }
      if (mode === 'readwrite' && writes.size > 0) {
        if (nextWriteFailure) {
          const error = nextWriteFailure;
          nextWriteFailure = null;
          throw error;
        }
        if (beforeCommit) beforeCommit({ writes: cloneValue([...writes.entries()]) });
        writes.forEach((record, key) => {
          if (record == null) records.delete(key);
          else records.set(key, cloneValue(record));
        });
      }
      return cloneValue(result);
    };
    const pending = tail.then(run, run);
    tail = pending.catch(() => {});
    return pending;
  };

  return {
    transact,
    failNextWrite(error = new CartPersistenceError('Injected transaction failure.')) {
      nextWriteFailure = error;
    },
    async dump() {
      await tail;
      return cloneValue([...records.values()]);
    },
    close: async () => {},
  };
}

export function createCartRepository({
  adapter,
  legacyStorage = browserLegacyStorage(),
  now = () => Date.now(),
  cartItemIdFactory = defaultCartItemId,
} = {}) {
  if (!adapter || typeof adapter.transact !== 'function') {
    throw new CartPersistenceError('A cart transaction adapter is required.');
  }
  let initializationPromise = null;

  const performInitialization = async () => {
    let legacyRaw = null;
    let legacyReadError = null;
    if (legacyStorage) {
      try {
        legacyRaw = legacyStorage.getItem(LEGACY_CART_STORAGE_KEY);
      } catch (error) {
        legacyReadError = error;
      }
    }

    const cart = await adapter.transact(
      [CART_RECORD_KEY, LEGACY_MIGRATION_KEY],
      'readwrite',
      transaction => {
        const existing = parseCartRecord(transaction.get(CART_RECORD_KEY));
        const migration = transaction.get(LEGACY_MIGRATION_KEY);
        let record = existing;
        if (!migration) {
          if (!record) {
            if (legacyReadError) {
              throw new CartPersistenceError('The legacy cart could not be read safely.', legacyReadError);
            }
            record = {
              ...emptyCartRecord(),
              items: parseLegacyCart(legacyRaw),
            };
            transaction.put(record);
          }
          transaction.put({
            key: LEGACY_MIGRATION_KEY,
            recordType: 'migration',
            schemaVersion: 1,
            completedAtMs: now(),
          });
        } else if (!record) {
          record = emptyCartRecord();
          transaction.put(record);
        }
        return publicCart(record);
      },
    );

    // Removal is deliberately after the atomic commit. If it fails, the
    // migration marker prevents the old cart from ever being imported twice.
    if (legacyStorage) {
      try {
        legacyStorage.removeItem(LEGACY_CART_STORAGE_KEY);
      } catch {
        // IndexedDB is already authoritative; leaving the inert legacy copy is safe.
      }
    }
    return cart;
  };

  const initialize = () => {
    if (!initializationPromise) {
      initializationPromise = performInitialization().catch(error => {
        initializationPromise = null;
        throw error;
      });
    }
    return initializationPromise;
  };

  const readCart = async () => {
    await initialize();
    return adapter.transact([CART_RECORD_KEY], 'readonly', transaction =>
      publicCart(transaction.get(CART_RECORD_KEY)),
    );
  };

  const updateCart = async mutator => {
    await initialize();
    return adapter.transact([CART_RECORD_KEY], 'readwrite', transaction => {
      const current = parseCartRecord(transaction.get(CART_RECORD_KEY)) || emptyCartRecord();
      const mutation = mutator(cloneValue(current.items));
      if (!mutation || !Array.isArray(mutation.items)) {
        throw new CartPersistenceError('A cart mutation returned invalid state.');
      }
      if (!mutation.changed) {
        return { cart: publicCart(current), changed: false };
      }
      const updated = cartRecordWithItems(current, mutation.items);
      transaction.put(updated);
      return { cart: publicCart(updated), changed: true };
    });
  };

  const addItem = async (product, selectedModifiers = [], quantityToAdd = 1) => {
    const productID = requireNonEmptyString(product?.id, 'product.id');
    if (!Number.isSafeInteger(quantityToAdd) || quantityToAdd <= 0) {
      throw new CartPersistenceError('quantityToAdd must be a positive safe integer.');
    }
    validateModifiers(selectedModifiers, 'selectedModifiers');
    const modifiers = cloneValue(selectedModifiers);
    return updateCart(items => {
      const candidate = { productID, selectedModifiers: modifiers };
      const configuration = cartConfigurationKey(candidate);
      const index = items.findIndex(item => cartConfigurationKey(item) === configuration);
      if (index >= 0) {
        const quantity = items[index].quantity + quantityToAdd;
        if (!Number.isSafeInteger(quantity)) {
          throw new CartPersistenceError('The cart quantity exceeds the safe integer range.');
        }
        const updated = [...items];
        updated[index] = { ...updated[index], quantity };
        return { items: updated, changed: true };
      }
      const id = requireNonEmptyString(cartItemIdFactory(), 'cartItemId');
      if (items.some(item => item.id === id)) {
        throw new CartPersistenceError('A duplicate cart item identifier was generated.');
      }
      return {
        items: [...items, { id, productID, quantity: quantityToAdd, selectedModifiers: modifiers }],
        changed: true,
      };
    });
  };

  const removeItem = itemId => {
    const id = requireNonEmptyString(itemId, 'itemId');
    return updateCart(items => {
      const updated = items.filter(item => item.id !== id);
      return { items: updated, changed: updated.length !== items.length };
    });
  };

  const updateQuantity = (itemId, newQuantity) => {
    const id = requireNonEmptyString(itemId, 'itemId');
    if (!Number.isSafeInteger(newQuantity)) {
      return Promise.reject(new CartPersistenceError('newQuantity must be a safe integer.'));
    }
    if (newQuantity < 1) return removeItem(id);
    return updateCart(items => {
      const index = items.findIndex(item => item.id === id);
      if (index < 0 || items[index].quantity === newQuantity) {
        return { items, changed: false };
      }
      const updated = [...items];
      updated[index] = { ...updated[index], quantity: newQuantity };
      return { items: updated, changed: true };
    });
  };

  const clear = () => updateCart(items => ({
    items: [],
    changed: items.length > 0,
  }));

  const markCheckoutSubmitted = async ({ userId, quoteId, purchasedItems }) => {
    const ledgerKey = reconciliationRecordKey(userId, 'quote', quoteId);
    const purchaseFingerprint = validatePurchasedItems(purchasedItems);
    await initialize();
    return adapter.transact([ledgerKey], 'readwrite', transaction => {
      const prior = transaction.get(ledgerKey);
      if (prior) {
        if (
          prior.recordType !== 'reconciliation' ||
          prior.schemaVersion !== RECONCILIATION_SCHEMA_VERSION ||
          prior.userId !== userId ||
          prior.referenceType !== 'quote' ||
          prior.referenceId !== quoteId ||
          prior.purchaseFingerprint !== purchaseFingerprint ||
          !['submitted', 'consumed'].includes(prior.state)
        ) {
          throw new CartReconciliationConflictError(
            'The checkout conflicts with its existing cart submission record.',
          );
        }
        return { state: prior.state, created: false };
      }
      transaction.put({
        key: ledgerKey,
        recordType: 'reconciliation',
        schemaVersion: RECONCILIATION_SCHEMA_VERSION,
        userId,
        referenceType: 'quote',
        referenceId: quoteId,
        purchaseFingerprint,
        state: 'submitted',
        submittedAtMs: now(),
      });
      return { state: 'submitted', created: true };
    });
  };

  const reconcilePurchasedItems = async ({
    userId,
    referenceType,
    referenceId,
    purchasedItems,
  }) => {
    const ledgerKey = reconciliationRecordKey(userId, referenceType, referenceId);
    const purchaseFingerprint = validatePurchasedItems(purchasedItems);
    await initialize();
    return adapter.transact([CART_RECORD_KEY, ledgerKey], 'readwrite', transaction => {
      const current = parseCartRecord(transaction.get(CART_RECORD_KEY)) || emptyCartRecord();
      const prior = transaction.get(ledgerKey);
      // A confirmation URL is not proof that this browser submitted the cart.
      // Missing local proof (old link, cleared storage, or another device)
      // preserves the current cart instead of creating a consumed record.
      if (!prior) {
        return { cart: publicCart(current), applied: false, eligible: false };
      }
      if (
        prior.recordType !== 'reconciliation' ||
        prior.schemaVersion !== RECONCILIATION_SCHEMA_VERSION ||
        prior.userId !== userId ||
        prior.referenceType !== referenceType ||
        prior.referenceId !== referenceId ||
        prior.purchaseFingerprint !== purchaseFingerprint ||
        !['submitted', 'consumed'].includes(prior.state)
      ) {
        throw new CartReconciliationConflictError(
          'The confirmed order conflicts with its existing cart reconciliation record.',
        );
      }
      if (prior.state === 'consumed') {
        return { cart: publicCart(current), applied: false, eligible: true };
      }

      const updated = cartRecordWithItems(
        current,
        subtractPurchasedItems(current.items, purchasedItems),
      );
      transaction.put(updated);
      transaction.put({
        ...prior,
        state: 'consumed',
        appliedAtMs: now(),
      });
      return { cart: publicCart(updated), applied: true, eligible: true };
    });
  };

  return {
    initialize,
    readCart,
    addItem,
    removeItem,
    updateQuantity,
    clear,
    markCheckoutSubmitted,
    reconcilePurchasedItems,
    close: () => adapter.close?.(),
  };
}

export function createBrowserCartRepository() {
  return createCartRepository({ adapter: createIndexedDbCartTransactionAdapter() });
}

export const cartStoreInternals = Object.freeze({
  CART_RECORD_KEY,
  LEGACY_MIGRATION_KEY,
  LEGACY_CART_STORAGE_KEY,
});
